import { uniqBy } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { type ConditionSpecsFragment } from '../../../../api/fragments/ConditionSpecs';
import { useGetBankQuery } from '../../../../api/queries/getBank';
import { useGetDealerQuery } from '../../../../api/queries/getDealer';
import { useGetInsurerQuery } from '../../../../api/queries/getInsurer';
import { useGetMobilityHomeDeliveryQuery } from '../../../../api/queries/getMobilityHomeDelivery';
import { useGetMobilityLocationQuery } from '../../../../api/queries/getMobilityLocation';
import { useGetModuleSpecsQuery } from '../../../../api/queries/getModuleSpecs';
import { useListLocationsQuery } from '../../../../api/queries/listLocations';
import { useListMobilityHomeDeliveriesQuery } from '../../../../api/queries/listMobilityHomeDeliveries';
import { type ConditionSettings, ConditionType } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { Condition } from './Condition';
import { ConditionGroup } from './ConditionGroup';

export type ConditionContainerProps = {
    conditionSettings: ConditionSettings;
    onClick: () => void;
    onDelete: () => void;
    logicCondition?: boolean;
};

const ApplicationConditionContainer = ({
    conditionSettings,
    onDelete,
    onClick,
    logicCondition,
}: ConditionContainerProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { applicationModuleConditionSettings } = conditionSettings;
    const { moduleId } = applicationModuleConditionSettings;
    const { data, loading } = useGetModuleSpecsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { moduleId },
    });

    if (loading) {
        return null;
    }

    return (
        <Condition
            label={t('consentsAndDeclarations:conditionLabels:isApplicationModule', {
                displayName: data.module.displayName,
            })}
            logicCondition={logicCondition}
            onClick={logicCondition ? onClick : onDelete}
        />
    );
};

const BankConditionContainer = ({ conditionSettings, onDelete, onClick, logicCondition }: ConditionContainerProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { bankConditionSettings } = conditionSettings;
    const { bankId } = bankConditionSettings;

    const { data, loading } = useGetBankQuery({
        fetchPolicy: 'cache-and-network',
        variables: { id: bankId },
    });

    if (loading) {
        return null;
    }

    return (
        <Condition
            label={t('consentsAndDeclarations:conditionLabels:isBank', {
                displayName: data.bank.displayName,
            })}
            logicCondition={logicCondition}
            onClick={logicCondition ? onClick : onDelete}
        />
    );
};

const DealerConditionContainer = ({
    conditionSettings,
    onDelete,
    onClick,
    logicCondition,
}: ConditionContainerProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { dealerConditionsSettings } = conditionSettings;
    const { dealerId } = dealerConditionsSettings;
    const { data, loading } = useGetDealerQuery({
        fetchPolicy: 'cache-and-network',
        variables: { id: dealerId },
    });

    if (loading) {
        return null;
    }

    return (
        <Condition
            label={t('consentsAndDeclarations:conditionLabels:isDealer', {
                displayName: data.getDealer.displayName,
            })}
            logicCondition={logicCondition}
            onClick={logicCondition ? onClick : onDelete}
        />
    );
};

const InsurerConditionContainer = ({
    conditionSettings,
    onDelete,
    onClick,
    logicCondition,
}: ConditionContainerProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { insurerConditionSettings } = conditionSettings;
    const { insurerId } = insurerConditionSettings;
    const { data, loading } = useGetInsurerQuery({
        fetchPolicy: 'cache-and-network',
        variables: { id: insurerId },
        skip: !insurerId,
    });

    if (loading) {
        return null;
    }

    return (
        <Condition
            label={t('consentsAndDeclarations:conditionLabels:isInsurer', {
                displayName: data.insurer.displayName,
            })}
            logicCondition={logicCondition}
            onClick={logicCondition ? onClick : onDelete}
        />
    );
};

const GiftVoucherConditionContainer = ({
    conditionSettings,
    onDelete,
    onClick,
    logicCondition,
}: ConditionContainerProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { giftVoucherConditionSettings } = conditionSettings;
    const { moduleId } = giftVoucherConditionSettings;
    const { data, loading } = useGetModuleSpecsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { moduleId },
    });

    if (loading) {
        return null;
    }

    return (
        <Condition
            label={t('consentsAndDeclarations:conditionLabels:isGiftVoucher', {
                displayName: data.module.displayName,
            })}
            logicCondition={logicCondition}
            onClick={logicCondition ? onClick : onDelete}
        />
    );
};

const LocationConditionContainer = ({
    conditionSettings,
    onDelete,
    onClick,
    logicCondition,
}: ConditionContainerProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { locationConditionSettings } = conditionSettings;
    const { locationId, isHomeDelivery } = locationConditionSettings;
    const company = useCompany(true);

    const { data: listLocationData } = useListLocationsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { companyId: company?.id } },
        skip: !locationId || isHomeDelivery,
    });

    const { data: listDeliveryData } = useListMobilityHomeDeliveriesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: company?.id,
            },
        },
    });

    const isMoreThanOneLocationsModule = uniqBy('moduleName', listLocationData?.locations?.items).length > 1;
    const isMoreThanOneHomeDeliveryModule =
        uniqBy(
            'id',
            listDeliveryData?.delivery?.items
                .filter(result => result.homeDelivery.isEnable)
                .flatMap(result => result.homeDelivery)
        ).length > 1;

    const { data, loading } = useGetMobilityLocationQuery({
        fetchPolicy: 'cache-and-network',
        variables: { locationId },
        skip: !locationId || isHomeDelivery,
    });

    const { data: deliveryData, loading: deliveryLoading } = useGetMobilityHomeDeliveryQuery({
        fetchPolicy: 'cache-and-network',
        variables: { deliveryId: locationId },
        skip: !locationId || !isHomeDelivery,
    });

    if (loading || deliveryLoading) {
        return null;
    }

    return (
        <Condition
            label={t('consentsAndDeclarations:conditionLabels:isLocation', {
                displayName: isHomeDelivery
                    ? t('common:homeDeliveryOption', {
                          moduleDisplayName: isMoreThanOneHomeDeliveryModule ? deliveryData?.delivery?.displayName : '',
                      })
                    : (t('common:pickUpLocation', {
                          locationPickUpName: data?.location?.name,
                          moduleDisplayName: isMoreThanOneLocationsModule ? data?.location.moduleName : '',
                      }) ?? ''),
            })}
            logicCondition={logicCondition}
            onClick={logicCondition ? onClick : onDelete}
        />
    );
};

const ConditionContainer = ({ conditionSettings, onClick, onDelete, logicCondition }: ConditionContainerProps) => {
    const { t } = useTranslation('consentsAndDeclarations');

    switch (conditionSettings.type) {
        case ConditionType.Or: {
            const { logicConditionSettings } = conditionSettings;
            const { children } = logicConditionSettings;
            const childrenConditions = children as ConditionSpecsFragment[];

            return (
                <ConditionGroup childrenConditions={childrenConditions} onClick={onClick} onGroupDelete={onDelete} />
            );
        }

        case ConditionType.IsApplicationModule: {
            return (
                <ApplicationConditionContainer
                    conditionSettings={conditionSettings}
                    logicCondition={logicCondition}
                    onClick={onClick}
                    onDelete={onDelete}
                />
            );
        }

        case ConditionType.IsBank: {
            return (
                <BankConditionContainer
                    conditionSettings={conditionSettings}
                    logicCondition={logicCondition}
                    onClick={onClick}
                    onDelete={onDelete}
                />
            );
        }

        case ConditionType.IsDealer: {
            return (
                <DealerConditionContainer
                    conditionSettings={conditionSettings}
                    logicCondition={logicCondition}
                    onClick={onClick}
                    onDelete={onDelete}
                />
            );
        }

        case ConditionType.IsInsurer: {
            return (
                <InsurerConditionContainer
                    conditionSettings={conditionSettings}
                    logicCondition={logicCondition}
                    onClick={onClick}
                    onDelete={onDelete}
                />
            );
        }

        case ConditionType.IsApplyingForFinancing:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:isApplyingForFinancing')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsApplyingForInsurance:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels.isApplyingForInsurance')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsApplicant:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:isApplicant')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsGuarantor:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:isGuarantor')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsCorporate:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:isCorporate')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsTestDrive:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:isTestDrive')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsTradeIn:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:isTradeIn')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.WithMyinfo:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:withMyinfo')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.WithoutMyinfo:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:withoutMyinfo')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsLocation:
            return (
                <LocationConditionContainer
                    conditionSettings={conditionSettings}
                    logicCondition={logicCondition}
                    onClick={onClick}
                    onDelete={onDelete}
                />
            );

        case ConditionType.IsTestDriveProcess:
            return (
                <Condition
                    label={t('consentsAndDeclarations:conditionLabels:isTestDriveProcess')}
                    logicCondition={logicCondition}
                    onClick={logicCondition ? onClick : onDelete}
                />
            );

        case ConditionType.IsGiftVoucher:
            return (
                <GiftVoucherConditionContainer
                    conditionSettings={conditionSettings}
                    logicCondition={logicCondition}
                    onClick={onClick}
                    onDelete={onDelete}
                />
            );

        default:
            return null;
    }
};

export default ConditionContainer;
